import {
  Alert,
  AlertTitle,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useResolveTerminalCountChangeRequestMutation } from "Api/Mutations/Merchants/useResolveTerminalCountChangeRequestMutation";
import { DismissTerminalCountRequestDialog } from "Components/Merchants/OrbitLOnboarding/DismissTerminalCountRequestDialog";
import {
  type TerminalCountChangeRecordDto,
  type TerminalCountChangeRequestDto,
  TerminalCountChangeStatus,
} from "Infrastructure/Api/Api";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";

const RESOURCES =
  Resources.Pages.Content.Merchants.OrbitLOnboarding.TerminalCountChange;

type Props = {
  merchantPublicID: string;
  contractedTerminalCount: number;
  contractedSoftPosCount: number;
  openRequest?: TerminalCountChangeRequestDto | null;
  history: Array<TerminalCountChangeRecordDto>;
  isEditingEnabled: boolean;
};

const formatChange = (record: TerminalCountChangeRecordDto, t: TFunction) => {
  const label = record.facilityName ?? t(RESOURCES.ContractLevelChange);
  const physical = `${record.oldCount} → ${record.newCount}`;
  if (record.requestedSoftPosCount == null) {
    return `${label}: ${physical}`;
  }
  const softPos = t(RESOURCES.SoftPosChange, {
    from: record.oldSoftPosCount ?? 0,
    to: record.requestedSoftPosCount,
  });
  return `${label}: ${physical}, ${softPos}`;
};

type OpenRequestAlertProps = {
  request: TerminalCountChangeRequestDto;
  isEditingEnabled: boolean;
  isPending: boolean;
  onResolve: () => void;
  onDismiss: () => void;
};

const OpenRequestAlert = ({
  request,
  isEditingEnabled,
  isPending,
  onResolve,
  onDismiss,
}: OpenRequestAlertProps) => {
  const { t } = useResource();

  return (
    <Alert
      severity="warning"
      action={
        isEditingEnabled ? (
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              variant="contained"
              disabled={isPending}
              onClick={onResolve}
            >
              {t(RESOURCES.ResolveButton)}
            </Button>
            <Button
              size="small"
              color="error"
              disabled={isPending}
              onClick={onDismiss}
            >
              {t(RESOURCES.DismissButton)}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <AlertTitle>{t(RESOURCES.OpenRequestHeading)}</AlertTitle>
      <Stack gap={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {request.oldTotal} → {request.newTotal}
        </Typography>
        {request.oldSoftPosTotal != null && (
          <Typography variant="body2" fontWeight={600}>
            {t(RESOURCES.SoftPosTotal, {
              from: request.oldSoftPosTotal,
              to: request.newSoftPosTotal,
            })}
          </Typography>
        )}
        {request.facilities.map((record) => (
          <Typography key={record.recordID} variant="body2">
            {formatChange(record, t)}
          </Typography>
        ))}
        {request.note && (
          <Typography variant="body2">{request.note}</Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {t(RESOURCES.RequestedBy, {
            user: request.requestedBy ?? t(RESOURCES.SystemActor),
          })}
          {` · ${formatDate(request.requestedAt, true)}`}
        </Typography>
        {isEditingEnabled && (
          <Typography variant="caption" color="text.secondary">
            {t(RESOURCES.ResolveHint)}
          </Typography>
        )}
      </Stack>
    </Alert>
  );
};

export const TerminalCountChangeSection = ({
  merchantPublicID,
  contractedTerminalCount,
  contractedSoftPosCount,
  openRequest,
  history,
  isEditingEnabled,
}: Props) => {
  const { t } = useResource();
  const [isDismissDialogOpen, setIsDismissDialogOpen] = useState(false);

  const { mutate: resolveRequest, isPending } =
    useResolveTerminalCountChangeRequestMutation({
      onSuccess: ({ isDismissed }) => {
        toast.success(
          t(isDismissed ? RESOURCES.DismissSuccess : RESOURCES.ResolveSuccess),
        );
        setIsDismissDialogOpen(false);
      },
      onError: () => toast.error(t(RESOURCES.Error)),
    });

  const resolvedHistory = history.filter(
    (record) => record.status !== TerminalCountChangeStatus.Pending,
  );

  return (
    <Stack gap={1.5}>
      <Typography variant="subtitle1" fontWeight={600}>
        {t(RESOURCES.SectionTitle)}
      </Typography>
      <Typography variant="body2">
        {t(RESOURCES.ContractTotal, { count: contractedTerminalCount })}
        {contractedSoftPosCount > 0 &&
          ` · ${t(RESOURCES.ContractSoftPosTotal, { count: contractedSoftPosCount })}`}
      </Typography>

      {openRequest && (
        <OpenRequestAlert
          request={openRequest}
          isEditingEnabled={isEditingEnabled}
          isPending={isPending}
          onResolve={() =>
            resolveRequest({
              merchantPublicID,
              requestGroupID: openRequest.requestGroupID,
              isDismissed: false,
            })
          }
          onDismiss={() => setIsDismissDialogOpen(true)}
        />
      )}

      <Typography variant="subtitle2" fontWeight={600}>
        {t(RESOURCES.HistoryHeading)}
      </Typography>
      {resolvedHistory.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.NoHistory)}
        </Typography>
      ) : (
        <Stack gap={0.75}>
          {resolvedHistory.map((record) => (
            <Stack
              key={record.recordID}
              direction="row"
              gap={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                label={t(RESOURCES.Status[record.status])}
                size="small"
                variant="outlined"
              />
              <Typography variant="body2">{formatChange(record, t)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t(RESOURCES.Source[record.source])}
                {record.changedBy ? ` · ${record.changedBy}` : ""}
                {` · ${formatDate(record.changedAt, true)}`}
              </Typography>
              {record.resolutionNote && (
                <Typography variant="caption" color="text.secondary">
                  {record.resolutionNote}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {isDismissDialogOpen && openRequest && (
        <DismissTerminalCountRequestDialog
          isDismissing={isPending}
          onClose={() => setIsDismissDialogOpen(false)}
          onConfirm={(note) =>
            resolveRequest({
              merchantPublicID,
              requestGroupID: openRequest.requestGroupID,
              isDismissed: true,
              note,
            })
          }
        />
      )}
    </Stack>
  );
};
